import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axiosInstance from "../../../services/server/axios";

import { useCurrency } from "../../../context/CurrencyContext";

const CryptomusPayment = ({ offer, discount, couponCode, onSuccess }) => {
  const { t } = useTranslation();
  const { formatPrice, currency, getSymbol } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [paymentUrl, setPaymentUrl] = useState('');
  const [uuid, setUuid] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);

  const handleCryptomusPayment = async () => { 
    try {
      setLoading(true);
      setErrorDetails(null);

      const amount = offer.montant - discount;

      const response = await axiosInstance.post('/cryptomus/create-payment', {
        amount: amount,
        currency: "USD",
        offer_id: offer.id,
        coupon_code: couponCode,
        discount: discount,
        success_url: `${window.location.origin}/cryptomus-callback?offer_id=${offer.id}`,
        callback_url: `${window.location.origin}/api/cryptomus/webhook`,
      });

      if (response.data.success) {
        const paymentUrl = response.data.payment_url;
        const uuid = response.data.uuid;
        const orderId = response.data.order_id;
        const achatOffreId = response.data.achat_offre_id;

        setPaymentUrl(paymentUrl);
        setUuid(uuid);

        // Check if we have a valid payment URL
        if (!paymentUrl) {
          toast.error(t('cryptomus.no_payment_url'));
          setLoading(false);
          return;
        }

        // Store payment info in localStorage for callback handling
        const paymentInfo = {
          uuid: uuid,
          orderId: orderId,
          achatOffreId: achatOffreId,
          offerId: offer.id,
          amount: amount,
          currency: "USD",
          couponCode: couponCode,
          discount: discount,
          timestamp: Date.now(),
          method: 'cryptomus'
        };
        localStorage.setItem('pendingCryptomusPayment', JSON.stringify(paymentInfo));

        // Open Cryptomus payment page in new tab
        const newWindow = window.open(paymentUrl, '_blank');

        // Start polling for payment status if we have UUID
        if (uuid) {
          pollPaymentStatus(uuid, achatOffreId);
        } else if (orderId) {
          // If no UUID but we have orderId, poll using orderId
          pollPaymentStatusByOrderId(orderId, achatOffreId);
        } else {
          // If neither UUID nor orderId, show manual check message
          toast.info(t('cryptomus.payment_initiated'));
          setLoading(false);
        }

        // Set up a listener for when the new tab might close or redirect back
        if (newWindow) {
          const checkWindowClosed = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(checkWindowClosed);
              // Window closed, check payment status
              checkPendingPayment();
            }
          }, 1000);
        }

      } else {
        // Store error details for debugging
        const errorMessage = response.data.error || t('cryptomus.failed_to_create');
        setErrorDetails({
          message: errorMessage,
          fullResponse: response.data
        });
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Cryptomus payment error:', error);

      // Extract more detailed error information
      const errorData = error.response?.data;

      // FIXED: Safely extract error message
      let errorMessage = '';
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string'
            ? errorData.error
            : JSON.stringify(errorData.error);
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else {
        errorMessage = error.message || t('cryptomus.payment_failed');
      }

      const errorCode = error.response?.data?.http_code || error.response?.status;

      setErrorDetails({
        message: errorMessage,
        code: errorCode,
        fullError: errorData,
        requestData: {
          amount: offer.montant - discount,
          currency: "USD",
          offer_id: offer.id,
          coupon_code: couponCode,
          discount: discount
        }
      });

      // FIXED: Use string methods safely
      const errorMessageStr = String(errorMessage).toLowerCase();

      // Show more informative error message
      if (errorMessageStr.includes('invalid merchant uuid') ||
        errorMessageStr.includes('invalid merchant') ||
        errorMessageStr.includes('merchant not found')) {
        toast.error(t('cryptomus.merchant_error'));
      } else if (errorMessageStr.includes('signature') ||
        errorMessageStr.includes('sign') ||
        errorMessageStr.includes('api key')) {
        toast.error(t('cryptomus.signature_error'));
      } else if (errorCode === 400) {
        toast.error(`${t('cryptomus.bad_request')} (400): ${errorMessage}`);
      } else if (errorCode === 401 || errorCode === 403) {
        toast.error(`${t('cryptomus.auth_error')} (${errorCode}): ${t('cryptomus.invalid_credentials')}`);
      } else if (errorCode === 422) {
        toast.error(`${t('cryptomus.validation_error')} (422): ${errorMessage}`);
      } else {
        toast.error(`${t('cryptomus.payment_failed')}: ${errorMessage}`);
      }

      setLoading(false);
    }
  };

  const pollPaymentStatus = async (uuid, achatOffreId) => {
    const maxAttempts = 60; // 5 minutes (5 seconds * 60)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await axiosInstance.get(`/cryptomus/payment-status/${uuid}`);

        if (response.data.success) {
          const status = response.data.status;

          if (status === 'paid' || status === 'paid_over') {
            // Payment successful
            onSuccess({
              paymentID: uuid,
              method: 'cryptomus',
              amount: offer.montant - discount,
              currency: "USD",
              status: 'completed',
              achat: { id: response.data.achat_offre_id },
            });
            setLoading(false);
            // Clear pending payment from localStorage
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'cancel' || status === 'fail') {
            // Payment failed
            toast.error(t('cryptomus.payment_cancelled'));
            setLoading(false);
            setPaymentUrl('');
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'paid_less') {
            // Partial payment
            toast.warning(t('cryptomus.partial_payment'));
            setLoading(false);
            setPaymentUrl('');
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'process' || status === 'check') {
            // Still processing, continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000);
            } else {
              toast.warning(t('cryptomus.payment_timeout'));
              setLoading(false);
              setPaymentUrl('');
              localStorage.removeItem('pendingCryptomusPayment');
            }
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          // Continue polling every 5 seconds
          setTimeout(checkStatus, 5000);
        } else {
          // Timeout
          toast.warning(t('cryptomus.payment_timeout'));
          setLoading(false);
          setPaymentUrl('');
          localStorage.removeItem('pendingCryptomusPayment');
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          toast.error(t('cryptomus.verify_failed'));
          setLoading(false);
          setPaymentUrl('');
          localStorage.removeItem('pendingCryptomusPayment');
        }
      }
    };

    // Start polling
    checkStatus();
  };

  const pollPaymentStatusByOrderId = async (orderId, achatOffreId) => {
    const maxAttempts = 60; // 5 minutes (5 seconds * 60)
    let attempts = 0;

    const checkStatus = async () => {
      try {
        const response = await axiosInstance.get(`/cryptomus/payment-status-by-order/${orderId}`);

        if (response.data.success) {
          const status = response.data.status;

          if (status === 'paid' || status === 'paid_over') {
            // Payment successful
            onSuccess({
              paymentID: orderId,
              method: 'cryptomus',
              amount: offer.montant - discount,
              currency: "USD",
              status: 'completed',
              achat: { id: response.data.achat_offre_id },
            });
            setLoading(false);
            // Clear pending payment from localStorage
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'cancel' || status === 'fail') {
            // Payment failed
            toast.error(t('cryptomus.payment_cancelled'));
            setLoading(false);
            setPaymentUrl('');
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'paid_less') {
            // Partial payment
            toast.warning(t('cryptomus.partial_payment'));
            setLoading(false);
            setPaymentUrl('');
            localStorage.removeItem('pendingCryptomusPayment');
            return;
          } else if (status === 'process' || status === 'check') {
            // Still processing, continue polling
            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(checkStatus, 5000);
            } else {
              toast.warning(t('cryptomus.payment_timeout'));
              setLoading(false);
              setPaymentUrl('');
              localStorage.removeItem('pendingCryptomusPayment');
            }
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          // Continue polling every 5 seconds
          setTimeout(checkStatus, 5000);
        } else {
          // Timeout
          toast.warning(t('cryptomus.payment_timeout'));
          setLoading(false);
          setPaymentUrl('');
          localStorage.removeItem('pendingCryptomusPayment');
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          toast.error(t('cryptomus.verify_failed'));
          setLoading(false);
          setPaymentUrl('');
          localStorage.removeItem('pendingCryptomusPayment');
        }
      }
    };

    // Start polling
    checkStatus();
  };

  const checkPendingPayment = async () => {
    try {
      const pendingPaymentStr = localStorage.getItem('pendingCryptomusPayment');
      if (!pendingPaymentStr) return;

      const pendingPayment = JSON.parse(pendingPaymentStr);
      const now = Date.now();
      const oneHour = 60 * 60 * 1000;

      // Check if payment info is older than 1 hour
      if (now - pendingPayment.timestamp > oneHour) {
        localStorage.removeItem('pendingCryptomusPayment');
        return;
      }

      // Check payment status
      if (pendingPayment.uuid) {
        const response = await axiosInstance.get(`/cryptomus/payment-status/${pendingPayment.uuid}`);
        if (response.data.success && (response.data.status === 'paid' || response.data.status === 'paid_over')) {
          // Payment completed, trigger success
          onSuccess({
            paymentID: pendingPayment.uuid,
            method: 'cryptomus',
            amount: pendingPayment.amount,
            currency: pendingPayment.currency,
            status: 'completed',
            achat: { id: pendingPayment.achatOffreId },
          });
          localStorage.removeItem('pendingCryptomusPayment');
        }
      } else if (pendingPayment.orderId) {
        const response = await axiosInstance.get(`/cryptomus/payment-status-by-order/${pendingPayment.orderId}`);
        if (response.data.success && (response.data.status === 'paid' || response.data.status === 'paid_over')) {
          // Payment completed, trigger success
          onSuccess({
            paymentID: pendingPayment.orderId,
            method: 'cryptomus',
            amount: pendingPayment.amount,
            currency: pendingPayment.currency,
            status: 'completed',
            achat: { id: pendingPayment.achatOffreId },
          });
          localStorage.removeItem('pendingCryptomusPayment');
        }
      }
    } catch (error) {
      console.error('Error checking pending payment:', error);
      // Don't show error to user, just log it
    }
  };

  // Check for pending payments when component mounts
  React.useEffect(() => {
    checkPendingPayment();

    // Also check when window gains focus (user returns from payment tab)
    const handleFocus = () => {
      checkPendingPayment();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  if (paymentUrl) {
    return (
      <div className="text-center p-3">
        <div className="alert alert-info">
          <h6>{t('cryptomus.payment_title')}</h6>
          <p>{t('cryptomus.payment_page_opened')}</p>
          <p>
            {t('cryptomus.page_did_not_open')}{" "}
            <a
              href={paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm btn-primary"
            >
              {t('cryptomus.click_to_pay')}
            </a>
          </p>
        </div>
        <div className="alert alert-warning small">
          <strong>{t('cryptomus.note')}:</strong> {t('cryptomus.dont_close_window')}
        </div>
        <button
          className="btn btn-secondary mt-2"
          onClick={() => {
            setPaymentUrl('');
            setLoading(false);
          }}
          disabled={loading}
        >
          {t('cryptomus.try_different_method')}
        </button>
      </div>
    );
  }

  return (
    <div className="cryptomus-payment">
      <button
        onClick={handleCryptomusPayment}
        disabled={loading}
        className="btn btn-primary w-100"
        style={{
          height: "48px",
          fontSize: "16px",
          opacity: loading ? 0.7 : 1
        }}
      >
        {loading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
            {t('cryptomus.preparing_payment')}
          </>
        ) : (
          `${t('cryptomus.pay_with_crypto')} - ${getSymbol()}${offer.montant - discount}`
        )}
      </button>




      <div className="mt-3">
        <div className="alert alert-info small">
          <strong>{t('cryptomus.payment_method')}:</strong> {t('cryptomus.payment_description')}
        </div>
      </div>


      <div className="d-flex mt-4 pt-4 justify-content-center align-items-center gap-4">
        <div className="p-2 rounded shadow-sm">
          <img
            src="/images/payments/bitcoin.png"
            alt="Revolut"
            className="img-fluid"
            style={{ height: "40px", width: "auto" }}
          />
        </div>
        <div className="p-2 rounded shadow-sm">
          <img
            src="/images/payments/usdt.png"
            alt="Monzo"
            className="img-fluid"
            style={{ height: "40px", width: "auto" }}
          />
        </div>
        <div className="p-2 rounded shadow-sm">
          <img
            src="/images/payments/ethereum.png"
            alt="Monzo"
            className="img-fluid"
            style={{ height: "40px", width: "auto" }}
          />
        </div>
      </div>

      {/* Error Debug Section */}
      {/* {errorDetails && (
        <div className="mt-3">
          <div className="alert alert-danger">
            <h6>{t('cryptomus.error_details')}</h6>
            <p><strong>{t('cryptomus.message')}:</strong> {String(errorDetails.message)}</p>
            {errorDetails.code && <p><strong>{t('cryptomus.code')}:</strong> {errorDetails.code}</p>}
            
            <details className="mt-2">
              <summary className="btn btn-sm btn-outline-danger">{t('cryptomus.show_debug_details')}</summary>
              <div className="mt-2 p-2 bg-dark text-light rounded small">
                <p><strong>{t('cryptomus.request_data')}:</strong></p>
                <pre className="mb-2">{JSON.stringify(errorDetails.requestData, null, 2)}</pre>
                
                {errorDetails.fullError && (
                  <>
                    <p><strong>{t('cryptomus.backend_response')}:</strong></p>
                    <pre>{JSON.stringify(errorDetails.fullError, null, 2)}</pre>
                  </>
                )}
                
                <p className="mt-2"><strong>{t('cryptomus.backend_fixes_needed')}:</strong></p>
                <ol className="mb-0">
                  <li>{t('cryptomus.fix_merchant_id')}</li>
                  <li>{t('cryptomus.fix_api_key')}</li>
                  <li>{t('cryptomus.fix_userid_header')}</li>
                  <li>{t('cryptomus.fix_signature_generation')}</li>
                  <li>{t('cryptomus.fix_checkpayment_signature')}</li>
                </ol>
              </div>
            </details>
            
            <div className="mt-2 small">
              <p className="mb-1"><strong>{t('cryptomus.quick_troubleshooting')}:</strong></p>
              <ul className="mb-0">
                <li>{t('cryptomus.troubleshoot_config')}</li>
                <li>{t('cryptomus.troubleshoot_account')}</li>
                <li>{t('cryptomus.troubleshoot_env')}</li>
                <li>{t('cryptomus.troubleshoot_logs')}</li>
              </ul>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default CryptomusPayment;